import {useState} from "react";
import {FaEllipsis} from "react-icons/fa6";

import {Icon} from "@/components/icon/Icon.tsx";
import {
  DropdownAction,
  DropdownActionItem,
  DropdownItem,
  DropDownReactLink,
} from "@/components/quickActionsBar/DropdownAction.tsx";
import {DropdownDivider} from "@/components/quickActionsBar/DropdownDivider.tsx";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {QUERY} from "@/pages/checks/constants.ts";
import {BulkAddToGroupCheckModal} from "@/pages/checks/list/actions/bulkAddToGroupCheck/BulkAddToGroupCheckModal.tsx";
import {BulkDeleteChecksModal} from "@/pages/checks/list/actions/bulkBulkDeleteChecks/BulkDeleteChecksModal.tsx";
import {BulkSetContactsModal} from "@/pages/checks/list/actions/bulkSetContacts/BulkSetContactsModal.tsx";
import {BulkSetEscalationsModal} from "@/pages/checks/list/actions/bulkSetEscalations/BulkSetEscalationsModal.tsx";
import {BulkSetIntervalsModal} from "@/pages/checks/list/actions/bulkSetIntervals/BulkSetIntervalsModal.tsx";
import {BulkSetLocationsModal} from "@/pages/checks/list/actions/bulkSetLocations/BulkSetLocationsModal.tsx";
import {BulkSetMaintenanceButtonAndModal} from "@/pages/checks/list/actions/bulkSetMaintenance/BulkSetMaintenanceButtonAndModal.tsx";
import {BulkSetSRTModal} from "@/pages/checks/list/actions/bulkSetSensitivityRetriesAndTimeout/BulkSetSRTModal.tsx";
import {BulkSetSlaModal} from "@/pages/checks/list/actions/bulkSetSLA/BulkSetSlaModal.tsx";
import {BulkSetTagsModal} from "@/pages/checks/list/actions/bulkSetTags/BulkSetTagsModal.tsx";
import {BulkUpgradeCheckerVersionModal} from "@/pages/checks/list/actions/bulkUpgradeCheckerVersion/BulkUpgradeCheckerVersionModal.tsx";

import {BulkPauseResumeActionButtonWithConfirmation} from "./BulkPauseResumeActionButtonWithConfirmation.tsx";
import {CheckActionType} from "./CheckActionType.tsx";

export interface BulkActionsButtonBarProps {
  selectedItemsIds: number[];
  onSuccess: () => void;
}

export const BulkActionsButtonBar = ({selectedItemsIds, onSuccess}: BulkActionsButtonBarProps) => {
  const [showBulkSetContactsButtonAndModal, setShowBulkSetContactsButtonAndModal] = useState(false);
  const [showBulkSetSRTButtonAndModal, setShowBulkSetSRTButtonAndModal] = useState(false);
  const [showBulkSetSlaButtonAndModal, setShowBulkSetSlaButtonAndModal] = useState(false);
  const [showBulkSetEscalationsButtonAndModal, setShowBulkSetEscalationsButtonAndModal] = useState(false);
  const [showBulkSetTagsButtonAndModal, setShowBulkSetTagsButtonAndModal] = useState(false);
  const [showBulkUpgradeCheckerVersionButtonAndModal, setShowBulkUpgradeCheckerVersionButtonAndModal] = useState(false);
  const [showBulkAddToGroupCheckButtonAndModal, setShowBulkAddToGroupCheckButtonAndModal] = useState(false);
  const [showBulkDeleteChecksButtonAndModal, setShowBulkDeleteChecksButtonAndModal] = useState(false);
  const [showBulkSetLocationsModal, setShowBulkSetLocationsModal] = useState(false);
  const [showBulkSetIntervalsModal, setShowBulkSetIntervalsModal] = useState(false);

  const dropdownItems: DropdownItem[] = [];

  dropdownItems.push(<DropdownActionItem text="Set Location" onClick={() => setShowBulkSetLocationsModal(true)} />);
  dropdownItems.push(<DropdownActionItem text="Set Intervals" onClick={() => setShowBulkSetIntervalsModal(true)} />);
  dropdownItems.push(<DropdownDivider />);
  dropdownItems.push(
    <DropdownActionItem text="Set Contacts" onClick={() => setShowBulkSetContactsButtonAndModal(true)} />
  );
  dropdownItems.push(
    <DropdownActionItem
      text="Set Sensitivity, Retries and Timeout"
      onClick={() => setShowBulkSetSRTButtonAndModal(true)}
    />
  );
  dropdownItems.push(<DropdownActionItem text="Set SLA" onClick={() => setShowBulkSetSlaButtonAndModal(true)} />);
  dropdownItems.push(
    <DropdownActionItem text="Set Escalations" onClick={() => setShowBulkSetEscalationsButtonAndModal(true)} />
  );
  dropdownItems.push(<DropdownActionItem text="Set Tags" onClick={() => setShowBulkSetTagsButtonAndModal(true)} />);
  dropdownItems.push(
    <DropdownActionItem
      text="Upgrade Checker Versions"
      onClick={() => setShowBulkUpgradeCheckerVersionButtonAndModal(true)}
    />
  );
  dropdownItems.push(<DropdownDivider />);
  dropdownItems.push(
    <DropDownReactLink
      to={`/checks/create?${QUERY.MONITORING_SERVICE_TYPE}=${MonitoringServiceType.GROUP}&${QUERY.GROUP_CHECK_SERVICES}=${selectedItemsIds.join(",")}`}
      text="Create Group Check"
    />
  );
  dropdownItems.push(
    <DropdownActionItem text="Add to Group Check" onClick={() => setShowBulkAddToGroupCheckButtonAndModal(true)} />
  );
  dropdownItems.push(
    <DropdownActionItem text="Delete Selected Checks" onClick={() => setShowBulkDeleteChecksButtonAndModal(true)} />
  );

  return (
    <>
      <BulkPauseResumeActionButtonWithConfirmation
        selectedItemsIds={selectedItemsIds}
        actionType={CheckActionType.PAUSE}
        onSuccess={onSuccess}
      />
      <BulkPauseResumeActionButtonWithConfirmation
        selectedItemsIds={selectedItemsIds}
        actionType={CheckActionType.RESUME}
        onSuccess={onSuccess}
      />
      <BulkSetMaintenanceButtonAndModal selectedItemsIds={selectedItemsIds} onSuccess={onSuccess} />

      <DropdownAction
        id={`bulkChecksDropdownActions`}
        items={dropdownItems}
        buttonContent={
          <>
            <Icon icon={FaEllipsis} />
            More
          </>
        }
      />

      <BulkSetContactsModal
        show={showBulkSetContactsButtonAndModal}
        setShow={setShowBulkSetContactsButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetSRTModal
        show={showBulkSetSRTButtonAndModal}
        setShow={setShowBulkSetSRTButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetSlaModal
        show={showBulkSetSlaButtonAndModal}
        setShow={setShowBulkSetSlaButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetEscalationsModal
        show={showBulkSetEscalationsButtonAndModal}
        setShow={setShowBulkSetEscalationsButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetTagsModal
        show={showBulkSetTagsButtonAndModal}
        setShow={setShowBulkSetTagsButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkUpgradeCheckerVersionModal
        show={showBulkUpgradeCheckerVersionButtonAndModal}
        setShow={setShowBulkUpgradeCheckerVersionButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkAddToGroupCheckModal
        show={showBulkAddToGroupCheckButtonAndModal}
        setShow={setShowBulkAddToGroupCheckButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkDeleteChecksModal
        show={showBulkDeleteChecksButtonAndModal}
        setShow={setShowBulkDeleteChecksButtonAndModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetLocationsModal
        show={showBulkSetLocationsModal}
        setShow={setShowBulkSetLocationsModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
      <BulkSetIntervalsModal
        show={showBulkSetIntervalsModal}
        setShow={setShowBulkSetIntervalsModal}
        selectedItemsIds={selectedItemsIds}
        onSuccess={onSuccess}
      />
    </>
  );
};
